import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { useTranslations } from 'next-intl';
import { ChangeEvent, ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

interface Choice {
    label: string;
    value: string;
}

interface Props {
    name: string;
    choices: Choice[];
    label?: ReactNode;
    error?: ViolationError;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    defaultValue?: string;
    disabled?: boolean;
}

export function RadioButtonInput({ name, choices, label, onChange, error, defaultValue, disabled = false }: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(error, onChange);

    return (
        <div className="flex max-w-full flex-col items-start gap-2">
            {label != null && <div className="text-sm">{label}</div>}

            <div className="flex max-md:flex-wrap max-md:gap-2">
                {choices.map(choice => (
                    <label
                        key={choice.value}
                        className={twJoin(
                            'flex min-w-10 cursor-pointer items-center justify-center overflow-hidden text-nowrap px-3 py-2 font-medium',
                            'border-neutral-300 max-md:rounded-md max-md:border',
                            'md:border-y md:border-l md:first:rounded-l-md md:last:rounded-r-md md:last:border-r',
                            'md:has-[:checked]:border md:has-[+label>input:checked]:border-r-0 md:[label:has(:checked)+&]:border-l-0',
                            'has-[:checked]:border-primary-500 has-[:checked]:bg-primary-200',
                            'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary-500 has-[:focus-visible]:ring-offset-2',
                        )}>
                        <input
                            className="absolute appearance-none"
                            name={name}
                            type="radio"
                            value={choice.value}
                            onChange={handleChange}
                            defaultChecked={defaultValue === choice.value}
                            disabled={disabled}
                        />
                        {choice.label}
                    </label>
                ))}
            </div>

            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
